import React, { useRef, useState } from 'react';
import { Checkbox, FormControlLabel, FormHelperText, FormControl, useTheme } from '@mui/material';
import { Controller } from 'react-hook-form';
import { generateTestId } from 'app/functions';
import HtmlContent from 'components/html/HtmlContent';

export const CheckboxHot = ({
  name,
  control,
  label,
  html,
  required = false,
  helperText,
  linksNotClickedErrorText: _linksNotClickedErrorText,
  rules = {},
  sx,
  testID,
  value: externalValue,
  onChange: externalOnChange,
  onAllLinksClicked,
  error: externalError,
  ...rest
}) => {
  const htmlContentRef = useRef();
  const [fieldError, setFieldError] = useState('');
  const theme = useTheme();

  const isControlled = !!control;
  const isExternalControlled = externalValue !== undefined && externalOnChange !== undefined;

  if (required && !rules.required) {
    rules.required = 'שדה חובה';
  }

  const handleLinkStatusChange = (status) => {
    const clickedAllLinks = status.totalLinks > 0 && status.clickedLinks.length === status.allLinks.length;
    if (clickedAllLinks && onAllLinksClicked) {
      onAllLinksClicked();
    }
  };

  const content = html || label || '';
  const displayError = externalError || fieldError;

  if (isControlled) {
    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field: { value, onChange, ...field }, fieldState: { error } }) => (
          <React.Fragment>
            <FormControl 
              error={!!displayError || !!error} 
              required={required}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                flexDirection: "row",
                gap: 0,
                ...sx
              }}
            >
              <FormControlLabel
                sx={{ 
                  margin: 0,
                  marginRight: 0,
                  '& .MuiFormControlLabel-label': {
                    marginLeft: 0,
                    marginRight: 0,
                  }
                }}
                control={
                  <Checkbox
                    testid={generateTestId(testID, `${name}-checkbox`)}
                    checked={!!value || value === 1 || value === '1'}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      setFieldError('');
                    }}
                    {...field}
                    {...rest}
                  />
                }
                label=""
              />
              <HtmlContent 
                className='hot-checkbox-label'
                ref={htmlContentRef}
                content={content}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onLinkStatusChange={handleLinkStatusChange}
              />
            </FormControl>
            <FormHelperText 
              sx={{
                marginTop: "-5px",
                marginLeft: "40px",
                minHeight: "1rem",
                color: theme.palette.text.error,
                fontSize: "0.75rem",
                visibility: (!!error?.message || displayError || helperText) ? 'visible' : 'hidden'
              }}
            >
              { displayError || error?.message || helperText || ' ' }
            </FormHelperText>
          </React.Fragment>
        )}
      />
    );
  }

  const [internalValue, setInternalValue] = useState(false);
  const checked = isExternalControlled ? externalValue : internalValue;

  const handleUncontrolledChange = (nextChecked) => {
    setFieldError('');
    setInternalValue(nextChecked);
    if (externalOnChange) {
      externalOnChange(nextChecked);
    }
  };

  return (
    <React.Fragment>
      <FormControl 
        error={!!displayError} 
        required={required}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          flexDirection: "row",
          gap: 0,
          ...sx
        }}
      >
        <FormControlLabel
          sx={{ 
            margin: 0,
            marginRight: 0,
            '& .MuiFormControlLabel-label': {
              marginLeft: 0,
              marginRight: 0,
            }
          }}
          control={
            <Checkbox
              testid={generateTestId(testID, `${name}-checkbox`)}
              checked={!!checked || checked === 1 || checked === '1'}
              onChange={(e) => handleUncontrolledChange(e.target.checked)}
              {...rest}
            />
          }
          label=""
        />
        <HtmlContent 
          className='hot-checkbox-label'
          ref={htmlContentRef}
          content={content}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onLinkStatusChange={handleLinkStatusChange}
        />
      </FormControl>
      <FormHelperText 
        sx={{
          marginTop: "-5px",
          marginLeft: "40px",
          minHeight: "1rem",
          color: theme.palette.text.error,
          fontSize: "0.75rem",
          visibility: (displayError || helperText) ? 'visible' : 'hidden'
        }}
      >
        { displayError || helperText || ' ' }
      </FormHelperText>
    </React.Fragment>
  );
};

export default CheckboxHot;
